import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Handshake, Users, ShieldCheck, ArrowRight, Sparkles } from 'lucide-react';
import { api } from '../services/api';
import { resolveBackendImage } from '../utils/brandLogo';

const TIPO_BADGE = {
  feira: 'bg-amber-100 text-amber-800',
  cooperativa: 'bg-green-100 text-green-800',
  associacao: 'bg-blue-100 text-blue-800',
  grupo: 'bg-purple-100 text-purple-800',
  rede: 'bg-pink-100 text-pink-800',
};
const TIPO_LABEL = {
  feira: 'Feira', cooperativa: 'Cooperativa', associacao: 'Associação',
  grupo: 'Grupo', rede: 'Rede',
};

export default function ColetivosWidget({ municipioSlug }) {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const params = {};
    if (municipioSlug) params.municipio_slug = municipioSlug;
    api.get('/v2/coletivos/destaque', { params })
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]))
      .finally(() => setLoaded(true));
  }, [municipioSlug]);

  if (!loaded || items.length === 0) return null;

  return (
    <section className="my-10" data-testid="coletivos-widget">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Handshake className="w-6 h-6 text-purple-600" />
            Coletivos da cidade
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Feiras, cooperativas e grupos que movimentam a economia local
          </p>
        </div>
        <Link
          to={municipioSlug ? `/${municipioSlug}/coletivos` : '/coletivos'}
          className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-purple-600 hover:text-purple-700"
          data-testid="coletivos-widget-ver-todos"
        >
          Ver todos <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.slice(0, 3).map((c) => (
          <Link
            key={c.id}
            to={municipioSlug ? `/${municipioSlug}/coletivos/${c.slug}` : `/coletivos/${c.slug}`}
            data-testid={`coletivo-widget-card-${c.id}`}
            className="group relative block rounded-xl overflow-hidden bg-white border border-gray-100 hover:border-purple-300 hover:shadow-md transition"
          >
            {/* Capa */}
            <div className="relative h-32 overflow-hidden bg-gradient-to-br from-purple-100 to-purple-200">
              {c.foto_capa && (
                <img
                  src={resolveBackendImage(c.foto_capa)}
                  alt={c.nome}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <span className={`absolute top-2 left-2 text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${TIPO_BADGE[c.tipo] || TIPO_BADGE.grupo}`}>
                {TIPO_LABEL[c.tipo] || c.tipo}
              </span>
              {c.verificado && (
                <span className="absolute top-2 right-2 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-blue-600 text-white text-[10px] font-bold">
                  <ShieldCheck className="w-3 h-3" />
                </span>
              )}
            </div>
            {/* Perfil sobreposto */}
            <div className="px-4 pt-3 pb-4 relative">
              {c.foto_perfil && (
                <img
                  src={resolveBackendImage(c.foto_perfil)}
                  alt=""
                  className="w-14 h-14 rounded-full object-cover absolute -top-7 right-4 border-4 border-white shadow-md"
                />
              )}
              <h3 className="font-bold text-lg leading-tight pr-12 group-hover:text-purple-700 transition">
                {c.nome}
              </h3>
              {c.descricao && (
                <p className="text-xs text-gray-500 mt-1 line-clamp-2">{c.descricao}</p>
              )}
              <div className="flex items-center gap-3 mt-3 text-xs text-gray-600">
                <span className="inline-flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  <strong>{c.total_membros_aprovados || 0}</strong> membros
                </span>
                {c.featured && (
                  <span className="inline-flex items-center gap-1 text-amber-600">
                    <Sparkles className="w-3 h-3" /> Destaque
                  </span>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="sm:hidden mt-4 text-center">
        <Link
          to={municipioSlug ? `/${municipioSlug}/coletivos` : '/coletivos'}
          className="inline-flex items-center gap-1 text-sm font-medium text-purple-600"
        >
          Ver todos os coletivos <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
}
